import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { FineDistributionPanel } from '@/pages/anatel/FineDistributionPanel';
import { OutcomeMixPanel } from '@/pages/anatel/OutcomeMixPanel';
import { ansCaseParams, fetchAnsOverview } from '@/services/ans';
import type { AnsBoardDateCoverage, AnsTrackSplit, AnswerOut } from '@/services/types';

/** The case list filtered to one predicate this screen counts by. */
export function ansCasesHref(filters: Parameters<typeof ansCaseParams>[0]): string {
  const params = ansCaseParams(filters).toString();
  return params ? `/ans/cases?${params}` : '/ans/cases';
}

/**
 * ANS penalties, and what the Diretoria Colegiada does to a first-instance fine.
 *
 * Every case in this corpus carries a first-instance decision and an amount,
 * so "resolved" here means BOARD-REVIEWED, which is less than half of the
 * corpus. Every rate on this page is over reviewed cases and says so, because
 * the same numerator against all cases is roughly half as large and both
 * sentences are true. The corpus block carries the snapshot competence the
 * rows describe: the portal re-dates the file daily, the data does not move.
 */
export function AnsOverviewPage() {
  const { t, i18n } = useTranslation(['ans', 'common']);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['ans', 'overview'],
    queryFn: fetchAnsOverview,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const stats: {
    key: string;
    value: number;
    href?: string;
    hrefLabel?: string;
    second?: string;
  }[] = [
    {
      key: 'cases',
      value: data.corpus.cases,
      href: ansCasesHref({}),
      hrefLabel: t('ans:overview.corpus.openCases'),
      second: t('ans:overview.corpus.competence', {
        competence: data.corpus.competence ?? '—',
      }),
    },
    {
      key: 'reviewed',
      value: data.corpus.reviewed,
      href: ansCasesHref({ board_outcome: 'any' }),
      hrefLabel: t('ans:overview.corpus.openReviewed'),
      second: t('ans:overview.corpus.boardDated', {
        n: number.format(data.corpus.board_dated),
      }),
    },
    {
      key: 'notReviewed',
      value: data.corpus.not_reviewed,
      href: ansCasesHref({ board_outcome: 'none' }),
      hrefLabel: t('ans:overview.corpus.openNotReviewed'),
      second: t('ans:overview.corpus.underAppeal', {
        n: number.format(data.corpus.under_appeal),
      }),
    },
    {
      key: 'entities',
      value: data.corpus.entities,
      href: '/ans/entities',
      hrefLabel: t('ans:overview.corpus.openEntities'),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ans:overview.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ans:overview.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ans:overview.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/ans/export/overview" label={t('ans:overview.exportOverview')} />
      </div>

      <AsOfStrip asOf={data.as_of} />

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.key} className="rounded-lg border bg-card p-3" data-tile={stat.key}>
            <dt className="text-xs text-muted-foreground">
              {t(`ans:overview.corpus.${stat.key}`)}
            </dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {stat.href ? (
                <Link
                  to={stat.href}
                  aria-label={stat.hrefLabel}
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {number.format(stat.value)}
                </Link>
              ) : (
                number.format(stat.value)
              )}
            </dd>
            {stat.second ? (
              <dd className="mt-0.5 text-xs text-muted-foreground">{stat.second}</dd>
            ) : null}
          </div>
        ))}
      </dl>

      <FieldGuide i18nKey="ans:overview.guide.corpus" level={2} />

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('ans:overview.waterfall.title')}
        </h2>
        <Answer answer={data.reduction_waterfall} help={t('ans:overview.waterfall.help')}>
          {(w) => (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Figure
                  label={t('ans:overview.waterfall.originalTotal')}
                  value={currency.format(w.original_total)}
                />
                <Figure
                  label={t('ans:overview.waterfall.finalTotal')}
                  value={currency.format(w.final_total)}
                />
                <Figure
                  label={t('ans:overview.waterfall.relief')}
                  value={currency.format(w.total_relief)}
                />
                <Figure
                  label={t('ans:overview.waterfall.medianRetained')}
                  value={w.median_retained === null ? '—' : percent.format(w.median_retained)}
                />
              </div>
              <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                {t('ans:overview.waterfall.bothDenominators', {
                  reduced: number.format(w.reduced),
                  reviewed: number.format(w.reviewed),
                  ofReviewed: percent.format(w.reduced_share_of_reviewed),
                  total: number.format(data.corpus.cases),
                  ofAll: percent.format(w.reduced / data.corpus.cases),
                })}
              </p>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="ans:overview.guide.waterfall" />
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <OutcomeMixPanel
          answer={{
            ...data.board_review_mix,
            value: data.board_review_mix.value?.outcomes ?? null,
          }}
          titleKey="ans:overview.boardMix.title"
          helpKey="ans:overview.boardMix.help"
          labelPrefix="ans:overview.outcomes."
          rowHref={(code) => ansCasesHref({ board_outcome: code })}
        />
        <FineDistributionPanel
          answer={data.penalty_distribution}
          titleKey="ans:overview.penalties.title"
          helpKey="ans:overview.penalties.help"
          countKey="ans:overview.penalties.count"
        />
      </div>

      <TrackSplitPanel answer={data.track_split} />

      <BoardDatesPanel answer={data.board_date_coverage} />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('ans:overview.refusals.title')}
        </h2>
        <p className="text-xs text-muted-foreground">{t('ans:overview.refusals.intro')}</p>
        <div className="grid gap-4 lg:grid-cols-3">
          <RefusalCard
            title={t('ans:overview.refusals.decisionMakers')}
            answer={data.decision_makers}
          />
          <RefusalCard title={t('ans:overview.refusals.reasoning')} answer={data.reasoning_layer} />
          <RefusalCard title={t('ans:overview.refusals.precedent')} answer={data.precedent_graph} />
        </div>
      </section>
    </div>
  );
}

/**
 * The track that originated the process: a consumer demand (NIP), a
 * representation, an institutional action or the Olho Vivo programme.
 * Descriptive, alphabetical, never a comparison of tracks as adjudicators.
 */
function TrackSplitPanel({ answer }: { answer: AnswerOut<AnsTrackSplit> }) {
  const { t, i18n } = useTranslation(['ans']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-track-split>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('ans:overview.tracks.title')}
      </h2>
      <Answer answer={answer} help={t('ans:overview.tracks.help')}>
        {(split) => (
          <div className="space-y-2">
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('ans:overview.tracks.track')}</th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:overview.tracks.cases')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:overview.tracks.reviewed')}
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      {t('ans:overview.tracks.share')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {split.rows.map((row) => (
                    <tr key={row.track} className="border-t" data-track={row.track}>
                      <td className="px-3 py-2 text-foreground">{row.track}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        <Link
                          to={ansCasesHref({ track: row.track })}
                          className="text-primary underline-offset-2 hover:underline"
                          aria-label={t('ans:overview.tracks.open', {
                            n: number.format(row.cases),
                            track: row.track,
                          })}
                        >
                          {number.format(row.cases)}
                        </Link>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {number.format(row.reviewed)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                        {percent.format(row.reviewed_share)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {split.unattributed > 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('ans:overview.tracks.unattributed', {
                  n: number.format(split.unattributed),
                })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </section>
  );
}

/**
 * The board outcome is recorded on more cases than the board decision date.
 * A PARTIAL answer while the gap exists; it disappears by itself if a later
 * competence fills the column, and its numbers are measured per request.
 */
function BoardDatesPanel({ answer }: { answer: AnswerOut<AnsBoardDateCoverage> }) {
  const { t, i18n } = useTranslation(['ans']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="rounded-lg border bg-card p-4" data-board-dates>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t('ans:overview.boardDates.title')}
      </h2>
      <Answer answer={answer} help={t('ans:overview.boardDates.help')}>
        {(coverage) => (
          <div className="grid grid-cols-3 gap-3">
            <Figure
              label={t('ans:overview.boardDates.reviewed')}
              value={number.format(coverage.reviewed)}
            />
            <Figure
              label={t('ans:overview.boardDates.dated')}
              value={number.format(coverage.dated)}
            />
            <Figure
              label={t('ans:overview.boardDates.missing')}
              value={`${number.format(coverage.missing)} (${percent.format(
                coverage.reviewed ? coverage.missing / coverage.reviewed : 0,
              )})`}
            />
          </div>
        )}
      </Answer>
    </section>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">{value}</div>
    </div>
  );
}
